<script setup lang="ts">
import {
  OrButtonV3,
  OrNotificationV3,
  useResponsive,
} from '@onereach/ui-components';
import { load as loadRecaptcha, getInstance as getCaptchaInstance } from 'recaptcha-v3';
import {
  ref,
  watch,
  onBeforeMount,
  onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useLoginForm } from '@/components/Auth/composables/useLoginForm';
import { useLoginPage } from '@/components/Auth/composables/useLoginPage';
import { useMfaForm } from '@/components/Auth/composables/useMfaForm';
import { useMfaRecoveryModal } from '@/components/Auth/composables/useMfaRecoveryModal';
import GoogleLogin from '@/components/Auth/GoogleLogin.vue';
import LoginForm from '@/components/Auth/LoginForm.vue';
import MFAForm from '@/components/Auth/MFAForm.vue';
import MFARecoveryModal from '@/components/Auth/MFARecoveryModal.vue';
import PasswordRecoveryModal from '@/components/Auth/PasswordRecoveryModal.vue';
import { ErrorActions } from '@/components/Auth/types/errors';
import OrAnimationLogo from '@/components/OrAnimationLogo/OrAnimationLogo.vue';
import { useAuth } from '@/composables/useAuth';
import useI18n from '@/composables/useI18n';
import config from '@/config';
import LoginContainer from '@/layouts/LoginContainer.vue';
import { useAuthStore } from '@/stores/auth/auth';

const props = withDefaults(defineProps<{
  shouldRedirectToDefaultProvider?: boolean;
  showGoogleSSo?: boolean;
}>(), {
  shouldRedirectToDefaultProvider: false,
  showGoogleSSo: false,
});

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { translate } = useI18n();
const { isDesktop } = useResponsive();

// Captcha logic
const showCaptchaV2Checkbox = ref(false);
const captchaWidgetId = ref<string | null>(null);
const captchaV2Checkbox = ref<HTMLElement | null>(null);

const getCaptchaToken = async (captchaV2CheckboxToken?: unknown) => {
  let captchaToken: string;
  if (typeof captchaV2CheckboxToken === 'string') {
    captchaToken = captchaV2CheckboxToken;
  } else {
    const recaptcha = await loadRecaptcha(config.GOOGLE_CAPTCHA_SITE_KEY_V3);
    captchaToken = await recaptcha.execute('signin');
  }
  return captchaToken;
};

const renderCapthcaV2Checkbox = async () => {
  showCaptchaV2Checkbox.value = true;
  // eslint-disable-next-line no-undef
  if (grecaptcha && !captchaWidgetId.value) {
    const captchaToken = await new Promise(resolve => {
      // eslint-disable-next-line no-undef
      captchaWidgetId.value = grecaptcha.render(captchaV2Checkbox.value as HTMLElement, {
        sitekey: config.GOOGLE_CAPTCHA_SITE_KEY_V2_CHECKBOX,
        // @ts-ignore
        callback: resolve,
      });
    });
    await submit(captchaToken);
    showCaptchaV2Checkbox.value = false;
  }
};

// LoginPage logic
const {
  loginIframeLogic,
  externalProviders,
  googleSSOLoading,
  accountData,
  isExpired,
  closeExpiredNotification,
} = useLoginPage();

const {
  initiateExternalLogin,
  authorizeRedirect,
  redirectToDefaultProviderIfExists,
} = useAuth();
const { showSkip, showSignup } = route.query;

watch(() => authStore.authorized, async (value) => {
  if (value) {
    authorizeRedirect('user-action').catch(() => isLoadingLogin.value = false);
  }
});

const processErrorAction = (action?: ErrorActions) => {
  if (action === ErrorActions.Expired) {
    const currentUrl = window.location.origin;
    const redirectUrl = `${currentUrl}?expired`;
    window.location.href = redirectUrl;
    return;
  } else if (action === ErrorActions.RenderCaptcha) {
    renderCapthcaV2Checkbox();
  }
};

const listenIframe = (event) => {
  loginIframeLogic(event, {
    keepLoggedIn,
    accountData,
  });
};

// Components composables
const {
  password,
  username,
  expiredUsername,
  keepLoggedIn,
  loginError,
  isLoadingLogin,
  submit,
  readonlyLogin,
  googleCode,
} = useLoginForm(
  accountData,
  getCaptchaToken,
  processErrorAction,
);

const {
  verificationCode,
  mfaError,
  isLoadingMfa,
  logout,
  pasteMfaFromBuffer,
  submitMfa,
} = useMfaForm(
  accountData,
  getCaptchaToken,
  processErrorAction,
  username,
  password,
  keepLoggedIn,
  googleCode,
);

const {
  isLoadingMfaRecovery,
  isOpenRecovery2fa,
  recoveryCode,
  recoveryError,
  submitMfaRecovery,
  toogleOpenRecovery2fa,
} = useMfaRecoveryModal(
  accountData,
  getCaptchaToken,
  processErrorAction,
  username,
  password,
  keepLoggedIn,
);

const resetPasswordModal = ref<typeof PasswordRecoveryModal | null>(null);
const showForgotPopup = () => {
  resetPasswordModal.value?.open();
};

// External providers && SSO logic
const isGoogleLogin = config.GOOGLE_LOGIN || props.showGoogleSSo;

if (props.shouldRedirectToDefaultProvider) {
  redirectToDefaultProviderIfExists(authStore.redirectPath);
}

onBeforeMount(async () => {
  window.addEventListener('message', listenIframe);

  await loadRecaptcha(config.GOOGLE_CAPTCHA_SITE_KEY_V3);

  if (route.query['google-sso-state']) {
    try {
      const ssoDataKey = route.query['google-sso-state'];
      const sessionStorageKey = `auth_state_${ssoDataKey as string}`;
      const user = JSON.parse(sessionStorage.getItem(sessionStorageKey) as string);
      const keepLoggedIn = localStorage.getItem('keepLoggedIn') === 'true';
      sessionStorage.removeItem(sessionStorageKey);
      localStorage.removeItem('keepLoggedIn');

      const data = user.token ? {
        type: 'SSO_SUCCESS_LOGIN',
        user,
      } : {
        type: 'SSO_TWO_FACTOR_CHECK',
        error: user.error,
        KEY: user.KEY,
      };

      loginIframeLogic({ data }, {
        keepLoggedIn,
        accountData,
      });
    } catch (error) {
      loginError.value = translate('login.login.Error during google sso');
    }

  }


  const isExpiredQuery = route.query.expired === null;
  if (isExpiredQuery) {
    if (expiredUsername.value) {
      username.value = expiredUsername.value;
      router.replace({ query: {} });
      isExpired.value = isExpiredQuery;
      readonlyLogin.value = true;
    } else {
      isExpired.value = true;
      router.replace({ query: {} });
    }
  }
});

onBeforeUnmount(() => {
  if (config.HELPER_BOT_ENABLED) {
    // Show chat on pages except login
    const chat = document.querySelector('.or-rwc') as HTMLElement;
    if (chat) {
      chat.style.display = 'block';
    }
  }
  getCaptchaInstance()?.hideBadge();
  window.removeEventListener('message', listenIframe);
});

const sendSkip = () => {
  window.parent.postMessage({ type: 'SKIP_LOGIN' }, '*');
};
const sendSignUp = () => {
  window.parent.postMessage({ type: 'SIGN_UP' }, '*');
};
</script>

<template>
  <LoginContainer>
    <div class="layout-row items-center flex-grow justify-center md:justify-between max-w-[1044px]">
      <div
        v-if="isDesktop"
        class="flex h-[504px] w-[492px] justify-center items-center"
      >
        <OrAnimationLogo />
      </div>

      <div
        v-if="isExpired"
        class="px-md absolute left-0 right-0 top-sm"
      >
        <OrNotificationV3
          class="sm:max-w-[768px] mx-auto"
          @close="closeExpiredNotification"
        >
          {{ translate('login.login.Due to inactivity, your session has expired') }}
        </OrNotificationV3>
      </div>

      <LoginForm
        v-if="!authStore.twoFactorCheck"
        v-model:keep-logged-in="keepLoggedIn"
        v-model:password="password"
        v-model:username="username"
        :is-loading="isLoadingLogin"
        :readonly-login="readonlyLogin"
        :service-error="loginError"
        :is-external-loading="googleSSOLoading"
        @submit="submit"
        @show-forgot-popup="showForgotPopup"
      >
        <template #captcha>
          <div v-show="showCaptchaV2Checkbox">
            <div
              ref="captchaV2Checkbox"
              class="flex justify-center mb-md+"
            />
          </div>
        </template>
        <template #google-login>
          <GoogleLogin
            v-if="isGoogleLogin"
            v-model="googleCode"
            v-model:google-loading="googleSSOLoading"
            :disabled="isLoadingLogin"
            button-type="outlined"
            :label="translate('login.login.Sign in with Google')"
            :keep-logged-in="keepLoggedIn"
            :account-data="accountData"
          />
        </template>
        <template #external-providers>
          <div
            v-if="externalProviders.length > 0"
            class="w-full layout-column gap-md"
          >
            <OrButtonV3
              v-for="provider in externalProviders"
              :key="provider.name"
              :loading="isLoadingLogin"
              variant="outlined"
              @click="initiateExternalLogin(provider.name, authStore.redirectPath)"
            >
              {{ translate('login.login.Sign in with', {providerName: provider.name}) }}
            </OrButtonV3>
          </div>
        </template>
        <template
          v-if="showSignup"
          #signup
        >
          <div class="grid gap-sm grid-cols-[1fr,max-content,1fr] items-center typography-body-2-semibold">
            <div
              class="border-t-1 border-outline-variant-dark"
            />
            {{ translate('login.login.Dont have an account') }}
            <div
              class="border-t-1 border-outline-variant-dark"
            />
          </div>
          <OrButtonV3
            variant="outlined"
            @click="sendSignUp"
          >
            {{ translate('login.login.Sign up') }}
          </OrButtonV3>
        </template>
        <template
          v-if="showSkip"
          #skip
        >
          <div
            v-if="!showSignup"
            class="border-t-1 border-outline-variant-dark"
          />
          <div class="grid auto-cols-max md:grid-flow-col items-center grid-flow-row justify-center md:justify-start">
            <div class="typography-body-2-regular">
              {{ showSignup ? translate('login.login.Not ready to make an account'): translate('login.login.Dont have an account') }}
            </div>
            <OrButtonV3
              variant="text"
              @click="sendSkip"
            >
              {{ translate('login.login.Continue as guest') }}
            </OrButtonV3>
          </div>
        </template>
      </LoginForm>
      <MFAForm
        v-else
        v-model:verification-code="verificationCode"
        :username="username"
        :is-loading="isLoadingMfa || googleSSOLoading"
        :error="mfaError"
        @submit="submitMfa"
        @toogle-open-recovery2fa="toogleOpenRecovery2fa(true)"
        @paste-from-buffer="pasteMfaFromBuffer"
        @logout="logout"
      >
        <template #captcha>
          <div v-show="showCaptchaV2Checkbox">
            <div
              ref="captchaV2Checkbox"
              class="flex justify-center mb-md+"
            />
          </div>
        </template>
      </MFAForm>
    </div>
    <MFARecoveryModal
      v-model="recoveryCode"
      :is-loading="isLoadingMfaRecovery || googleSSOLoading"
      :recovery-error="recoveryError"
      :is-open-recovery2fa="isOpenRecovery2fa"
      @toogle-open-recovery2fa="toogleOpenRecovery2fa"
      @submit="submitMfaRecovery"
    />
    <PasswordRecoveryModal
      ref="resetPasswordModal"
      :redirect-path="authStore.redirectPath"
    />
  </LoginContainer>
</template>
